import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

import { attachInterceptors } from './interceptors'

export type HttpConfig<T = unknown> = AxiosRequestConfig<T>

// ---------- Axios Instance ----------
export const apiClient: AxiosInstance = axios.create({
  timeout: 20_000,
  headers: {
    Accept: 'application/json',
  },
})

// Attach interceptors
attachInterceptors(apiClient)

// ---------- Helpers ----------
function unwrap<T>(res: AxiosResponse<T>): T {
  return res.data
}

// ---------- Data-first HTTP wrapper ----------
export const http = {
  get<T>(url: string, config?: HttpConfig): Promise<T> {
    return apiClient.get<T>(url, config).then(unwrap)
  },

  post<T, B = unknown>(url: string, body?: B, config?: HttpConfig): Promise<T> {
    return apiClient.post<T>(url, body, config).then(unwrap)
  },

  put<T, B = unknown>(url: string, body?: B, config?: HttpConfig): Promise<T> {
    return apiClient.put<T>(url, body, config).then(unwrap)
  },

  patch<T, B = unknown>(url: string, body?: B, config?: HttpConfig): Promise<T> {
    return apiClient.patch<T>(url, body, config).then(unwrap)
  },

  delete<T>(url: string, config?: HttpConfig): Promise<T> {
    return apiClient.delete<T>(url, config).then(unwrap)
  },
}

// ---------- Optional: small utility for AbortController ----------
export function withSignal(config: HttpConfig | undefined, signal?: AbortSignal): HttpConfig {
  return { ...(config ?? {}), signal }
}

// ---------- Utility to skip authentication ----------
/**
 * Creates a config object that prevents the interceptor from adding an Authorization header.
 * Use this for public endpoints that don't require authentication.
 *
 * @example
 * ```ts
 * const config = await http.get('/api/config', withoutAuth())
 * ```
 */
export function withoutAuth(): HttpConfig {
  return {
    headers: {
      Authorization: null,
    },
  }
}
